import React, { useMemo, useCallback } from 'react';
import './Coupons.scss';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import { ServerResponsiveTable } from '../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction } from '../../types/responsiveTable';
import { useClient } from '../../hooks/useClient';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { PrimeIcons } from 'primereact/api';
import { Button } from 'primereact/button';
import { useResourcePanel } from '../../hooks/useResourcePanel';
import { Coupon } from '../../types/coupon';
import { CouponPanel } from '../../components/CouponPanel/CouponPanel';
import { showDeleteDialog } from '../../utils/dialogUtils';
import { hasFeaturesPermission } from '../../utils/featureUtils';
import { ALL_FEATURES } from '../../config/features';
import { useAuthContext } from '../../context/AuthContext';
import { ExportButton } from '../../components/ExportButton/ExportButton';
import { mutate } from 'swr';

export const getCouponDeleteConfirmationDialogProps = (coupon: Coupon | undefined) => {
  return {
    message: (
      <>
        ¿Estás seguro de que quieres eliminar el bono <b>{coupon?.name}</b>?
        <br />
        <br />
        Ya no se podrá asignar a nuevas reservas ni aparecerá en la lista de bonos, pero los usuarios que tengan este bono
        asignado lo podrán seguir usando.
      </>
    ),
    header: 'Eliminar bono',
  };
};

export const Coupons = () => {
  const { get, delete: del } = useClient();
  const { organization } = useOrganizationContext();
  const { features, featuresLoading } = useAuthContext();

  const {
    isEditing,
    setIsEditing,
    panelVisible,
    closePanel,
    selectedResource,
    setSelectedResource,
    openPanel: originalOpenPanel,
    isDeleting,
    setIsDeleting,
  } = useResourcePanel<Coupon>(undefined);

  const openPanel = useCallback(
    async (isEditing: boolean, resourceOrId?: Coupon | string) => {
      if (typeof resourceOrId === 'string') {
        const coupon = await get<Coupon>(`/coupons/${resourceOrId}`);
        originalOpenPanel(isEditing, coupon);
      } else {
        originalOpenPanel(isEditing, resourceOrId);
      }
    },
    [get, originalOpenPanel],
  );

  const handleDelete = useCallback(
    async (id: string | undefined) => {
      if (!id || !organization?.id) return false;
      setIsDeleting(true);

      const response = await del<Coupon>(`/coupons/${id}`, {
        errorMessages: {
          summary: 'No se pudo eliminar el bono',
          defaultDetail: 'Hubo un error al intentar borrar el bono',
          RESOURCE_IN_USE: 'El bono no se puede eliminar porque está siendo usado',
        },
        successMessage: {
          summary: 'Bono eliminado',
          detail: 'El bono se ha eliminado correctamente',
        },
      });

      if (response) {
        // The table reads `.../coupons/paginated?page=…` (useServerTable):
        // revalidate it, whatever page, sort or filter it shows (#73).
        const tableKey = `/organizations/${organization.id}/coupons/paginated?`;
        mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
      }
      setIsDeleting(false);
      return !!response;
    },
    [organization, del, setIsDeleting],
  );

  const tableHeaders: TableColumn<Coupon>[] = [
    {
      label: 'Nombre',
      accessor: 'name',
      type: 'string',
      isTitle: true,
    },
    {
      label: 'Precio',
      accessor: 'price',
      type: 'number',
      valueFromCallBack: (value) => {
        return new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
        }).format(value);
      },
      sortable: true,
      isCurrency: true,
      currencyLocale: 'es-ES',
      currencyCode: 'EUR',
    },
    {
      label: 'Usos',
      accessor: 'usageLimit',
      type: 'number',
    },
  ];

  const userHasEditFeature = hasFeaturesPermission([ALL_FEATURES.BONUSES], { features, featuresLoading });

  const options: TableAction[] = useMemo(
    () => [
      {
        label: 'Ver',
        onClick: (id) => {
          openPanel(false, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Editar',
        onClick: (id) => {
          openPanel(true, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Eliminar',
        onClick: async (id) => {
          const coupon = selectedResource?.id === id ? selectedResource : await get<Coupon>(`/coupons/${id}`);
          showDeleteDialog(() => handleDelete(id), getCouponDeleteConfirmationDialogProps(coupon));
        },
        hasPermission: userHasEditFeature,
      },
    ],
    [userHasEditFeature, openPanel, handleDelete, get, selectedResource],
  );

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<Coupon[]>) => (
      <div className='coupons-header-options'>
        <Button
          label='Añadir bono'
          outlined
          rounded
          icon={PrimeIcons.PLUS}
          onClick={() => {
            openPanel(false);
          }}
        />
        <ExportButton fetchData={fetchAllForExport} columns={tableHeaders} fileName='Bonos' disabled={isDeleting} />
      </div>
    ),
    [openPanel, tableHeaders, isDeleting],
  );

  return (
    <PageTemplate className='coupones-page' title='Bonos'>
      <ServerResponsiveTable
        tableId='coupons'
        endpoint={organization ? `/organizations/${organization.id}/coupons/paginated` : ''}
        disabled={isDeleting}
        columns={tableHeaders}
        options={options}
        headerOptions={renderHeaderOptions}
        onRowClick={(row) => {
          openPanel(false, row.id);
        }}
      />
      <CouponPanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={closePanel}
        onIsEditingChange={(value) => setIsEditing(value)}
        isEditing={isEditing}
        onDeleteResource={handleDelete}
        isDeleting={isDeleting}
      />
    </PageTemplate>
  );
};
